/**
 * Human approval of AI-assistant writes (ALC-3889).
 *
 * Stable refusal codes are translated here so UI copy follows the active locale.
 */
export const writeConfirmationsPtBr = {
  page: {
    title: 'Aprovações do assistente',
    description:
      'Suas solicitações ao assistente e alterações financeiras que você pode revisar para colegas. Pagamentos e destinos de pagamento exigem aprovação de outra pessoa autorizada.',
    loadError: {
      title: 'Falha ao carregar as aprovações',
      description: 'Atualize a página e tente novamente.',
    },
    loading: 'Carregando aprovações do assistente',
    empty: 'Nenhuma alteração aguardando aprovação.',
    emptyTitle: 'Nenhuma aprovação encontrada',
    filteredEmptyTitle: 'Nenhuma aprovação {{status}} encontrada',
    filteredEmpty: 'Nenhuma aprovação com status {{status}} corresponde ao filtro atual.',
    backToList: 'Voltar para as aprovações',
    filters: {
      status: 'Status',
      allStatuses: 'Todos os status',
      active: 'Filtros ativos',
      clear: 'Limpar todos',
    },
    pagination: {
      summary: 'Mostrando {{start}}–{{end}} de {{total}} aprovações',
    },
    sort: {
      createdAt: 'Ordenar por data da solicitação',
      expiresAt: 'Ordenar por data de expiração',
      tooltip: 'Alternar ordem de classificação',
    },
    table: {
      operation: 'Operação',
      resource: 'Registro',
      status: 'Status',
      requestedAt: 'Solicitada em',
      expiresAt: 'Expira em',
    },
  },
  detail: {
    title: 'Revisar alteração',
    loading: 'Carregando solicitação de aprovação',
    notFound: 'Esta solicitação de aprovação não foi encontrada.',
    loadError: 'Não foi possível carregar esta solicitação de aprovação.',
    independentApproval:
      'Você solicitou esta alteração. Peça a outra pessoa autorizada para revisar e aprovar pelo link desta página.',
    readOnly:
      'Você pode revisar esta solicitação, mas não tem permissão para aprovar ou rejeitar a alteração.',
    operation: 'Operação',
    resource: 'Registro',
    expiresAt: 'Expira em',
    requestedAt: 'Solicitada em',
    changes: 'Alterações propostas',
    exactPatch: 'Conteúdo exato da alteração',
    exactPatchHint:
      'Os dados exatos que serão aplicados. Números de conta bancária aparecem mascarados.',
    exactPatchToggle: 'Ver conteúdo completo',
    changesEmpty: 'Nenhum campo será alterado.',
    field: 'Campo',
    from: 'Antes',
    to: 'Depois',
    emptyValue: '—',
  },
  status: {
    PENDING: 'Aguardando aprovação',
    APPROVED: 'Aprovada',
    REJECTED: 'Rejeitada',
    CONSUMED: 'Aplicada',
  },
  actions: {
    approve: 'Aprovar',
    reject: 'Rejeitar',
    review: 'Revisar',
    retry: 'Tentar novamente',
  },
  approveDialog: {
    title: 'Aprovar esta alteração?',
    description:
      'A aprovação vale para exatamente esta alteração e pode ser usada uma única vez. Depois de aprovada, o assistente aplica a alteração — e ela não é desfeita automaticamente.',
    confirm: 'Aprovar alteração',
    cancel: 'Cancelar',
    success: 'Alteração aprovada. O assistente já pode aplicá-la.',
  },
  rejectDialog: {
    title: 'Rejeitar esta alteração?',
    description:
      'A alteração não será aplicada. Esta decisão é definitiva: o assistente precisará solicitar a alteração novamente.',
    confirm: 'Rejeitar alteração',
    cancel: 'Cancelar',
    success: 'Alteração rejeitada.',
  },
  errors: {
    MCP_CONFIRMATION_NOT_FOUND: 'Esta solicitação de aprovação não foi encontrada.',
    MCP_CONFIRMATION_NOT_APPROVED: 'Esta alteração não foi aprovada.',
    MCP_CONFIRMATION_EXPIRED:
      'Esta solicitação de aprovação expirou. Solicite a alteração novamente para gerar uma nova.',
    MCP_CONFIRMATION_ALREADY_USED:
      'Esta aprovação já foi utilizada. Cada aprovação permite exatamente uma alteração.',
    MCP_CONFIRMATION_MISMATCH:
      'A alteração solicitada não corresponde mais ao que foi aprovado. Solicite a alteração novamente.',
    MCP_CONFIRMATION_REQUIRES_USER:
      'Esta alteração precisa da aprovação de uma pessoa e não pode ser solicitada por uma integração sem usuário.',
    MCP_CONFIRMATION_APPROVAL_FORBIDDEN: 'Você não tem permissão para aprovar esta alteração.',
    MCP_CONFIRMATION_APPROVAL_REQUIRES_SESSION:
      'Esta alteração só pode ser aprovada por uma pessoa conectada ao Alchemy ERP.',
    generic: 'Não foi possível concluir esta ação. Tente novamente.',
  },
} as const;

export const writeConfirmationsEn = {
  page: {
    title: 'Assistant approvals',
    description:
      'Your assistant requests and financial changes you can review for colleagues. Payments and payment destinations require another authorized person’s approval.',
    loadError: {
      title: 'Could not load approvals',
      description: 'Refresh the page and try again.',
    },
    loading: 'Loading assistant approvals',
    empty: 'No changes are waiting for approval.',
    emptyTitle: 'No approvals found',
    filteredEmptyTitle: 'No {{status}} approvals found',
    filteredEmpty: 'No approvals with status {{status}} match the current filter.',
    backToList: 'Back to approvals',
    filters: {
      status: 'Status',
      allStatuses: 'All statuses',
      active: 'Active filters',
      clear: 'Clear all',
    },
    pagination: {
      summary: 'Showing {{start}}–{{end}} of {{total}} approvals',
    },
    sort: {
      createdAt: 'Sort by requested date',
      expiresAt: 'Sort by expiry date',
      tooltip: 'Toggle sort order',
    },
    table: {
      operation: 'Operation',
      resource: 'Record',
      status: 'Status',
      requestedAt: 'Requested',
      expiresAt: 'Expires',
    },
  },
  detail: {
    title: 'Review change',
    loading: 'Loading approval request',
    notFound: 'This approval request was not found.',
    loadError: 'Could not load this approval request.',
    independentApproval:
      'You requested this change. Ask another authorized person to review and approve it using this page’s link.',
    readOnly:
      'You can review this request, but do not have permission to approve or reject the change.',
    operation: 'Operation',
    resource: 'Record',
    expiresAt: 'Expires',
    requestedAt: 'Requested',
    changes: 'Proposed changes',
    exactPatch: 'Exact change payload',
    exactPatchHint: 'The exact data that will be applied. Bank account numbers appear masked.',
    exactPatchToggle: 'Show full payload',
    changesEmpty: 'No fields will change.',
    field: 'Field',
    from: 'Before',
    to: 'After',
    emptyValue: '—',
  },
  status: {
    PENDING: 'Waiting for approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    CONSUMED: 'Applied',
  },
  actions: {
    approve: 'Approve',
    reject: 'Reject',
    review: 'Review',
    retry: 'Try again',
  },
  approveDialog: {
    title: 'Approve this change?',
    description:
      'The approval covers exactly this change and can be used once. Once approved, the assistant applies it — and it is not undone automatically.',
    confirm: 'Approve change',
    cancel: 'Cancel',
    success: 'Change approved. The assistant can now apply it.',
  },
  rejectDialog: {
    title: 'Reject this change?',
    description:
      'The change will not be applied. This is final: the assistant will have to request it again.',
    confirm: 'Reject change',
    cancel: 'Cancel',
    success: 'Change rejected.',
  },
  errors: {
    MCP_CONFIRMATION_NOT_FOUND: 'This approval request was not found.',
    MCP_CONFIRMATION_NOT_APPROVED: 'This change has not been approved.',
    MCP_CONFIRMATION_EXPIRED: 'This approval request expired. Request the change again.',
    MCP_CONFIRMATION_ALREADY_USED:
      'This approval was already used. Each approval allows exactly one change.',
    MCP_CONFIRMATION_MISMATCH:
      'The requested change no longer matches what was approved. Request the change again.',
    MCP_CONFIRMATION_REQUIRES_USER:
      'This change needs approval by a person and cannot be requested by an integration without a user.',
    MCP_CONFIRMATION_APPROVAL_FORBIDDEN: 'You do not have permission to approve this change.',
    MCP_CONFIRMATION_APPROVAL_REQUIRES_SESSION:
      'This change can only be approved by a person signed in to Alchemy ERP.',
    generic: 'We could not complete this action. Try again.',
  },
} as const;
